import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, MoonStars, PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { AdminModal, BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";

function DigestPreview({ data, onClose }) {
  return (
    <AdminModal eyebrow={`To ${data.to || "no owner email"}`} title={data.subject} onClose={onClose} testId="evening-digest-preview">
      <iframe title="Evening summary preview" srcDoc={data.html} sandbox="" className="h-[620px] w-full border-2 border-black bg-white"
              data-testid="evening-digest-preview-frame" />
    </AdminModal>
  );
}

// Super admin: the 6pm Eastern evening summary email (on/off, preview, send one now).
export function EveningDigestCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/admin/evening-digest").then((r) => setS(r.data)).catch(() => setS(false));
  }, []);
  if (!s) return null;
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errText(e, "Something went wrong")); } finally { setBusy(""); }
  };
  const toggle = () => run("toggle", async () => {
    const { data } = await api.put("/admin/evening-digest", { enabled: !s.enabled });
    setS(data);
    toast.success(data.enabled ? "Evening summary is on" : "Evening summary is off");
  });
  const showPreview = () => run("preview", async () => setPreview((await api.get("/admin/evening-digest/preview")).data));
  const sendNow = () => run("send", async () => {
    const { data } = await api.post("/admin/evening-digest/send-now");
    if (data.status !== "sent") throw new Error("send failed");
    toast.success(`Sent to ${data.to}`);
    setS((await api.get("/admin/evening-digest")).data);
  });
  return (
    <div className="brut-card mb-6 flex flex-wrap items-center gap-3 p-3 text-xs" data-testid="evening-digest-card">
      <MoonStars size={16} weight="bold" />
      <div className="min-w-[220px] flex-1">
        <div className="font-chivo font-black uppercase tracking-tighter">Evening summary</div>
        <div className="text-[11px] text-zinc-600" data-testid="evening-digest-info">
          Every day at {s.send_time} to {s.to || "no owner email"} — leads, deliveries, money and call taps per buyer vs yesterday.
          {s.last_sent_at && <> Last sent {fmtDate(s.last_sent_at)} ({s.last_status}).</>}
        </div>
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-[11px] uppercase tracking-widest">
        <input type="checkbox" className="accent-black" checked={s.enabled} disabled={busy === "toggle"} onChange={toggle}
               data-testid="evening-digest-toggle" /> {s.enabled ? "On" : "Off"}
      </label>
      <button type="button" className={BTN_WHITE} onClick={showPreview} disabled={!!busy} data-testid="evening-digest-preview-btn">
        <Eye size={12} weight="bold" /> {busy === "preview" ? "Building…" : "Preview"}
      </button>
      <button type="button" className={BTN_BLACK} onClick={sendNow} disabled={!!busy || !s.to} data-testid="evening-digest-send-btn">
        <PaperPlaneRight size={12} weight="bold" /> {busy === "send" ? "Sending…" : "Send me one now"}
      </button>
      {preview && <DigestPreview data={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
